import { useState, useEffect } from 'react';
import {
  Drawer,
  Table,
  Button,
  message,
  Spin,
  Empty,
  Tag,
  Switch,
  Grid,
  InputNumber,
  Tooltip,
  Input,
  Space,
  Typography,
} from 'antd';
import { DeleteOutlined, LoadingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { useGetSingleRFID, useUpdateRFID } from '../../Pages/rfid-manager/Hook';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useAuthStore } from '../../store/useAuthStore';
import { useQueryClient } from '@tanstack/react-query';
import {
  DebouncedSelect,
  fetchLocationList,
  fetchChargerList,
  fetchCustomerList,
} from '../useDebouncedSelectLocation';
import { formatUnixToLocalDate, formatKwFromWatts } from '../../utils/utils';

const { useBreakpoint } = Grid;
const { TextArea } = Input;
const { Title, Text } = Typography;

const BRAND = '#067C60';
const CANVAS = '#FFFFFF';
const BORDER = '#F0F0F0';

const FieldLabel = ({ children, required }) => (
  <div className="mb-1.5 text-[13px] font-medium text-[#525252]">
    {children}
    {required ? <span className="text-[#F04438] ml-0.5">*</span> : null}
  </div>
);

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
};

const StatBlock = ({ label, value, tone = 'default', tooltip }) => {
  const toneClass = {
    default: 'text-[#1B2740]',
    success: 'text-[#12B76A]',
    warning: 'text-[#F79009]',
    danger: 'text-[#F04438]',
    info: 'text-[#2E90FA]',
    muted: 'text-[#A3A3A3]',
  }[tone];

  const content = (
    <div className="h-full">
      <div className="text-[12px] font-medium text-[#737373] mb-0.5">{label}</div>
      <div className={`text-[15px] font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );

  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
};

StatBlock.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.oneOf(['default', 'success', 'warning', 'danger', 'info', 'muted']),
  tooltip: PropTypes.string,
};

const ViewRFIDDrawer = ({ chargerData, setOpen, open, refetchRFIDList }) => {
  const { t } = useTranslation('ViewRFID');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { operatorId } = useOperatorStore();
  const { user } = useAuthStore();
  const isReadOnlyUser = user?.role === 'READ_ONLY_ADMIN';
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useGetSingleRFID({
    id: chargerData?._id,
    operatorId,
  });

  const { mutate: updateRFID, isPending: isUpdating } = useUpdateRFID(
    chargerData?._id
  );

  const [localLocations, setLocalLocations] = useState([]);
  const [localChargers, setLocalChargers] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [isGlobal, setIsGlobal] = useState(chargerData?.isGlobal || false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [limitAmount, setLimitAmount] = useState(0);
  const [rfidTag, setRfidTag] = useState(chargerData?.RFID || '');
  const [description, setDescription] = useState(chargerData?.description || '');

  useEffect(() => {
    if (data) {
      const locs = (data.locations || []).map((loc) => ({
        _id: loc._id,
        locationName: loc.locationName ?? loc.name ?? '-',
        address: loc.address ?? { city: '', state: '' },
      }));
      setLocalLocations(locs);
      const chs = (data.chargers || []).map((ch) => ({
        _id: ch._id,
        name: ch.name ?? '-',
        serialNumber: ch.serialNumber ?? '',
        status: ch.status ?? '',
      }));
      setLocalChargers(chs);
      setIsGlobal(data.isGlobal ?? false);
      setRfidTag(data.RFID ?? '');
      setDescription(data.description ?? '');
      setLimitAmount(data.limit != null ? Number(data.limit) / 1000 : 0);
      if (data.customer?._id) {
        const cust = data.customer;
        const label =
          `${cust?.fname || ''} ${cust?.lname || ''}`.trim() +
          (cust?.phoneNumber ? ` - ${cust.phoneNumber}` : '');
        setSelectedCustomer({
          value: cust._id,
          label: label || cust._id,
        });
      } else {
        setSelectedCustomer(null);
      }
    }
  }, [data]);

  const handleRemoveLocation = (locationId) => {
    if (isReadOnlyUser) return;
    setLocalLocations((prev) => prev.filter((loc) => loc._id !== locationId));
  };

  const handleRemoveCharger = (chargerId) => {
    if (isReadOnlyUser) return;
    setLocalChargers((prev) =>
      prev.filter((charger) => charger._id !== chargerId)
    );
  };

  const handleAddLocation = (_, option) => {
    if (isReadOnlyUser) return;
    if (!localLocations.some((loc) => loc._id === option.value)) {
      const locationData = option.data || {};

      setLocalLocations([
        ...localLocations,
        {
          _id: option.value,
          locationName: locationData.locationName || option.label.split(' - ')[0],
          address: locationData.address || {
            city: '',
            state: '',
          },
        },
      ]);
      setSelectedLocation(null);
    }
  };

  const handleAddCharger = (_, option) => {
    if (isReadOnlyUser) return;
    if (!localChargers.some((charger) => charger._id === option.value)) {
      const chargerOptionData = option.data || {};

      setLocalChargers([
        ...localChargers,
        {
          _id: option.value,
          name: chargerOptionData.name || option.label.split(' - ')[0],
          serialNumber: chargerOptionData.serialNumber || '',
          status: chargerOptionData.status || '',
        },
      ]);
      setSelectedCharger(null);
    }
  };

  const handleUpdateRFID = () => {
    if (isReadOnlyUser) return;

    const trimmedTag = (rfidTag || '').trim();
    if (!trimmedTag) {
      message.error(t('ViewRFIDDrawer.RFIDRequired'));
      return;
    }
    if (trimmedTag.length > 20) {
      message.error(t('ViewRFIDDrawer.RFIDMax'));
      return;
    }

    const customerIdValue =
      selectedCustomer?.value ?? selectedCustomer ?? null;
    const payload = {
      operatorId,
      RFID: trimmedTag,
      customerId: customerIdValue ? String(customerIdValue) : '',
      locationId: isGlobal ? [] : localLocations.map((loc) => loc._id),
      chargersId: isGlobal ? [] : localChargers.map((charger) => charger._id),
      description: (description || '').trim(),
      isGlobal,
      ...(!(data?.prePaid ?? chargerData?.prePaid)
        ? { limit: (Number(limitAmount) || 0) * 1000 }
        : {}),
    };
    updateRFID(payload, {
      onSuccess: () => {
        message.success(t('ViewRFIDDrawer.UpdateSuccess'));
        refetch();
        refetchRFIDList?.();
        queryClient.invalidateQueries({ queryKey: ['customer-details'] });
        queryClient.invalidateQueries({ queryKey: ['fetch-RFID'] });
        setOpen(false);
      },
      onError: (error) => {
        message.error(error?.response?.data?.message || t('ViewRFIDDrawer.UpdateError'));
      },
    });
  };

  const rfidData = data ?? chargerData;
  const status = rfidData?.status ?? '-';
  const isAccepted = status === 'Accepted';

  const getMonthlyUsageMeta = () => {
    const monthly = Number(rfidData?.monthlyUsageSoFar ?? 0);
    const limit = Number(rfidData?.limit ?? 0);
    const overLimit = limit > 0 && monthly >= limit;
    const nearLimit = limit > 0 && monthly >= limit * 0.75 && !overLimit;

    let tone = 'muted';
    if (limit > 0) {
      const ratio = monthly / limit;
      if (ratio >= 1) tone = 'danger';
      else if (ratio >= 0.75) tone = 'warning';
      else if (monthly > 0) tone = 'success';
    } else if (monthly > 0) {
      tone = 'success';
    }

    const tooltip = overLimit
      ? t('RFIDTable.MonthlyLimitReached', { ns: 'rfid' })
      : nearLimit
        ? t('RFIDTable.MonthlyLimitApproaching', { ns: 'rfid' })
        : undefined;

    const value =
      limit > 0
        ? t('ViewRFIDDrawer.MonthlyUsageOfLimit', {
            used: formatKwFromWatts(monthly),
            limit: formatKwFromWatts(limit),
          })
        : `${formatKwFromWatts(monthly)} kW`;

    return { tone, tooltip, value };
  };

  const monthlyMeta = getMonthlyUsageMeta();
  const lifetime = Number(rfidData?.lifetimeUsageSoFar ?? 0);

  const renderRFIDDetails = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <div className="sm:col-span-2">
          <FieldLabel required>{t('ViewRFIDDrawer.RFIDLabel')}</FieldLabel>
          <Input
            value={rfidTag}
            maxLength={20}
            disabled={isReadOnlyUser}
            onChange={(e) => setRfidTag(e.target.value)}
            placeholder={t('ViewRFIDDrawer.RFIDPlaceholder')}
            className="font-mono"
          />
        </div>

        <div className="sm:col-span-2">
          <FieldLabel>{t('ViewRFIDDrawer.AssignedTo')}</FieldLabel>
          <DebouncedSelect
            placeholder={t('ViewRFIDDrawer.SearchCustomerPlaceholder')}
            fetchOptions={(search) =>
              fetchCustomerList({ operatorId, search })
            }
            allowClear
            labelInValue
            disabled={isReadOnlyUser}
            value={selectedCustomer}
            onChange={setSelectedCustomer}
            style={{ width: '100%' }}
          />
          <p className="mt-1 mb-0 text-xs text-[#737373]">
            {t('ViewRFIDDrawer.AssignHelpText')}
          </p>
        </div>

        <div>
          <FieldLabel>{t('ViewRFIDDrawer.LimitLabel')}</FieldLabel>
          <InputNumber
            min={0}
            step={0.01}
            value={limitAmount}
            disabled={isReadOnlyUser}
            onChange={setLimitAmount}
            addonAfter="kW"
            className="w-full!"
          />
          <p className="mt-1 mb-0 text-xs text-[#737373]">
            {t('ViewRFIDDrawer.LimitHelpText')}
          </p>
        </div>

        <div className="flex flex-col justify-start sm:pt-6">
          <div className="flex items-center gap-2.5">
            <Switch
              checked={isGlobal}
              disabled={isReadOnlyUser}
              onChange={setIsGlobal}
              size="small"
            />
            <span className="text-[13px] font-medium text-[#3E4E69]">
              {t('ViewRFIDDrawer.GlobalCheckbox')}
            </span>
            <Tooltip title={t('ViewRFIDDrawer.GlobalTooltip')}>
              <InfoCircleOutlined className="text-[#A3A3A3] shrink-0" />
            </Tooltip>
          </div>
          {isGlobal ? (
            <p className="mt-1.5 mb-0 text-xs text-[#737373] leading-relaxed">
              {t('ViewRFIDDrawer.GlobalTooltip')}
            </p>
          ) : null}
        </div>

        <div className="sm:col-span-2">
          <FieldLabel>{t('ViewRFIDDrawer.Description')}</FieldLabel>
          <TextArea
            value={description}
            disabled={isReadOnlyUser}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder={t('ViewRFIDDrawer.DescriptionPlaceholder')}
          />
        </div>
      </div>

      <div className="pt-4 border-t border-[#F0F0F0]">
        <Title level={5} className="mb-3!">
          {t('ViewRFIDDrawer.UsageTitle')}
        </Title>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatBlock
            label={t('ViewRFIDDrawer.MonthlyUsage')}
            value={monthlyMeta.value}
            tone={monthlyMeta.tone}
            tooltip={monthlyMeta.tooltip}
          />
          <StatBlock
            label={t('ViewRFIDDrawer.LifetimeUsage')}
            value={`${formatKwFromWatts(lifetime)} kW`}
            tone={lifetime > 0 ? 'info' : 'muted'}
          />
        </div>
      </div>
    </div>
  );

  const renderLocationsSection = () => (
    <div className="mt-5 pt-4 border-t border-[#F0F0F0]">
      <Title level={5} className="mb-3!">
        {t('ViewRFIDDrawer.LocationsTitle')}
      </Title>
      <DebouncedSelect
        placeholder={t('ViewRFIDDrawer.AddLocationPlaceholder')}
        className="w-full sm:w-72"
        disabled={isReadOnlyUser}
        fetchOptions={(search) =>
          fetchLocationList({ operatorId, search })
        }
        value={selectedLocation}
        onChange={handleAddLocation}
      />
      {localLocations.length > 0 ? (
        <Table
          columns={[
            {
              title: t('ViewRFIDDrawer.Table.LocationName'),
              dataIndex: 'locationName',
              key: 'locationName',
            },
            {
              title: t('ViewRFIDDrawer.Table.City'),
              dataIndex: ['address', 'city'],
              key: 'city',
              render: (_, record) => record.address?.city || '-',
            },
            {
              title: t('ViewRFIDDrawer.Table.State'),
              dataIndex: ['address', 'state'],
              key: 'state',
              render: (_, record) => record.address?.state || '-',
            },
            {
              title: t('ViewRFIDDrawer.Table.Action'),
              key: 'action',
              width: 72,
              align: 'center',
              render: (_, record) => (
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  disabled={isReadOnlyUser}
                  onClick={() => handleRemoveLocation(record._id)}
                />
              ),
            },
          ]}
          dataSource={localLocations}
          pagination={false}
          rowKey="_id"
          size="small"
          className="mt-3"
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-4">
          <Empty description={t('ViewRFIDDrawer.NoLocations')} />
        </div>
      )}
    </div>
  );

  const renderChargersSection = () => (
    <div className="mt-5 pt-4 border-t border-[#F0F0F0]">
      <Title level={5} className="mb-3!">
        {t('ViewRFIDDrawer.ChargersTitle')}
      </Title>
      <DebouncedSelect
        className="w-full sm:w-72"
        placeholder={t('ViewRFIDDrawer.AddChargerPlaceholder')}
        disabled={isReadOnlyUser}
        fetchOptions={(search) =>
          fetchChargerList({ operatorId, search })
        }
        value={selectedCharger}
        onChange={handleAddCharger}
      />
      {localChargers.length > 0 ? (
        <Table
          columns={[
            {
              title: t('ViewRFIDDrawer.Table.SerialNumber') || 'Serial Number',
              dataIndex: 'serialNumber',
              key: 'serialNumber',
            },
            {
              title: t('ViewRFIDDrawer.Table.Name'),
              dataIndex: 'name',
              key: 'name',
            },
            {
              title: t('ViewRFIDDrawer.Table.Status') || 'Status',
              dataIndex: 'status',
              key: 'status',
              render: (chargerStatus) => (
                <Tag color={chargerStatus === 'ONLINE' ? 'green' : 'red'} variant="outlined">
                  {chargerStatus}
                </Tag>
              ),
            },
            {
              title: t('ViewRFIDDrawer.Table.Action'),
              key: 'action',
              width: 72,
              align: 'center',
              render: (_, record) => (
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  disabled={isReadOnlyUser}
                  onClick={() => handleRemoveCharger(record._id)}
                />
              ),
            },
          ]}
          dataSource={localChargers}
          pagination={false}
          rowKey="_id"
          size="small"
          className="mt-3"
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-4">
          <Empty description={t('ViewRFIDDrawer.NoChargers')} />
        </div>
      )}
    </div>
  );

  return (
    <Drawer
      maskClosable={false}
      destroyOnHidden
      footer={
        <div className="flex justify-end gap-2 items-center">
          <Button onClick={() => setOpen(false)}>
            {t('ViewRFIDDrawer.CancelButton')}
          </Button>
          {!isReadOnlyUser && (
            <Button
              type="primary"
              loading={isUpdating}
              onClick={handleUpdateRFID}
            >
              {t('ViewRFIDDrawer.UpdateButton')}
            </Button>
          )}
        </div>
      }
      open={open}
      title={
        <div className="pr-6 flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
          <Title level={4} className="mb-0! mt-0! font-mono truncate">
            {rfidData?.RFID ?? t('ViewRFIDDrawer.Title')}
          </Title>
          <Space size={6} wrap className="shrink-0">
            <Tag color={isAccepted ? 'success' : 'error'} className="m-0!">
              {status}
            </Tag>
          </Space>
          <Text type="secondary" className="text-xs! sm:ml-auto shrink-0">
            {t('ViewRFIDDrawer.CreatedAt')}:{' '}
            <Text>
              {rfidData?.created_at
                ? formatUnixToLocalDate(rfidData.created_at)
                : '—'}
            </Text>
          </Text>
        </div>
      }
      width={isMobile ? '100%' : 720}
      onClose={() => setOpen(false)}
      styles={{
        header: {
          borderInlineStart: `3px solid ${BRAND}`,
          borderBottom: `1px solid ${BORDER}`,
          paddingBlock: 12,
        },
        body: {
          background: CANVAS,
          padding: isMobile ? 16 : 20,
        },
        footer: {
          borderTop: `1px solid ${BORDER}`,
        },
      }}
    >
      {isLoading ? (
        <div className="flex justify-center p-10">
          <Spin indicator={<LoadingOutlined spin style={{ fontSize: 24 }} />} />
        </div>
      ) : (
        <>
          {renderRFIDDetails()}
          {!isGlobal && (
            <>
              {renderLocationsSection()}
              {renderChargersSection()}
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

ViewRFIDDrawer.propTypes = {
  chargerData: PropTypes.shape({
    _id: PropTypes.string,
    RFID: PropTypes.string,
    isGlobal: PropTypes.bool,
    status: PropTypes.string,
    description: PropTypes.string,
    created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    updated_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customer: PropTypes.object,
    balance: PropTypes.number,
    limit: PropTypes.number,
    monthlyUsageSoFar: PropTypes.number,
    lifetimeUsageSoFar: PropTypes.number,
  }),
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  refetchRFIDList: PropTypes.func,
};

export default ViewRFIDDrawer;
